import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { setupTotp, verifyTotpSetup, type MfaSetupData } from '../lib/api';
import AuthLayout from './AuthLayout';

type Step = 'intro' | 'scan' | 'verify' | 'codes' | 'done';

export default function MfaSetupPage() {
  const [searchParams] = useSearchParams();
  const projectId = useMemo(() => searchParams.get('project_id') ?? '', [searchParams]);
  const accessToken = useMemo(() => searchParams.get('access_token') ?? '', [searchParams]);

  const [step, setStep] = useState<Step>('intro');
  const [setupData, setSetupData] = useState<MfaSetupData | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showManualKey, setShowManualKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (step !== 'scan' || setupData) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setupTotp(projectId, accessToken).then(result => {
      if (cancelled) return;
      if (result.success && result.data) {
        setSetupData(result.data);
      } else {
        setError(result.error ?? 'Unable to start MFA setup.');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [step, setupData, projectId, accessToken]);

  if (!projectId || !accessToken) {
    return (
      <AuthLayout
        title="Sign in required"
        subtitle="Open MFA setup from your account; this page needs an access token."
      >
        <p className="auth-help">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  async function handleVerify(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setLoading(true);
    const result = await verifyTotpSetup(projectId, accessToken, code.trim());
    setLoading(false);

    if (result.success && result.data) {
      setRecoveryCodes(result.data.recovery_codes);
      setStep('codes');
    } else {
      setError(result.error ?? 'Verification failed. Try a fresh code from your app.');
    }
  }

  if (step === 'intro') {
    return (
      <AuthLayout
        title="Set up two-factor authentication"
        subtitle="Add a second step to sign-in using an authenticator app."
      >
        <ol className="auth-steps">
          <li>Install an authenticator app (1Password, Authy, Google Authenticator, etc).</li>
          <li>Scan the QR code we’ll show you on the next step.</li>
          <li>Enter the 6-digit code your app generates to confirm.</li>
        </ol>
        <button type="button" className="auth-submit" onClick={() => setStep('scan')}>
          Get started
        </button>
      </AuthLayout>
    );
  }

  if (step === 'scan') {
    return (
      <AuthLayout
        title="Scan this QR code"
        subtitle="Open your authenticator app and add a new account by scanning the code."
      >
        {loading && !setupData ? <p>Generating QR code…</p> : null}
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        {setupData ? (
          <>
            <div className="auth-qr" dangerouslySetInnerHTML={{ __html: setupData.qr_code_svg }} />
            <button
              type="button"
              className="auth-secondary"
              onClick={() => setShowManualKey(prev => !prev)}
            >
              {showManualKey ? 'Hide manual setup key' : 'Can’t scan? Show manual key'}
            </button>
            {showManualKey ? (
              <p className="auth-help">
                Enter this key manually:
                <br />
                <code>{setupData.secret}</code>
              </p>
            ) : null}
            <button type="button" className="auth-submit" onClick={() => setStep('verify')}>
              I’ve added the account
            </button>
          </>
        ) : null}
      </AuthLayout>
    );
  }

  if (step === 'verify') {
    return (
      <AuthLayout
        title="Confirm your code"
        subtitle="Enter the current 6-digit code from your authenticator app to finish enrolling."
      >
        <form onSubmit={handleVerify} className="auth-form" noValidate>
          <label className="auth-field">
            <span>Verification code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              required
              value={code}
              onChange={event => setCode(event.target.value)}
              disabled={loading}
            />
          </label>

          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Verifying…' : 'Verify and enable'}
          </button>
          <button
            type="button"
            className="auth-secondary"
            onClick={() => setStep('scan')}
            disabled={loading}
          >
            Back
          </button>
        </form>
      </AuthLayout>
    );
  }

  if (step === 'codes') {
    return (
      <AuthLayout
        title="Save your recovery codes"
        subtitle="Each code can be used once if you lose access to your authenticator."
      >
        <ul className="auth-codes">
          {recoveryCodes.map(rc => (
            <li key={rc}>
              <code>{rc}</code>
            </li>
          ))}
        </ul>
        <p className="auth-help">
          Store them in a password manager or print them. We can’t show them again.
        </p>
        <button type="button" className="auth-submit" onClick={() => setStep('done')}>
          I’ve saved my codes
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Two-factor enabled" subtitle="MFA is now active on your account.">
      <p className="auth-help">
        <Link to="/mfa/settings">Manage MFA</Link>
      </p>
    </AuthLayout>
  );
}
